import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { transferIdToSessionM, lookupMarianaTekUser } from '../services/api'

const PlatformIDsSection = styled.div`
  grid-column: 1 / -1;
  margin-top: 2rem;
  padding: 2rem;
  background: ${theme.color.background.light};
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const PlatformIDsTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 1.5rem;
`

const PlatformIDsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
`

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem;
  border-bottom: 2px solid ${theme.color.border.light};
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.color.border.light};
`

const TableCell = styled.td`
  padding: 1rem;
  font-size: 1.4rem;
  color: ${theme.color.text.base};
`

const PlatformName = styled.td`
  padding: 1rem;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const TransferSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid ${theme.color.border.light};
`

const TransferTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-right: 1rem;
`

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SelectLabel = styled.label`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${theme.color.text.grey};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Select = styled.select`
  padding: 0.8rem 1.2rem;
  font-size: 1.4rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }

  &:disabled {
    background: ${theme.color.background.light};
    cursor: not-allowed;
  }
`

const TransferButton = styled.button`
  padding: 1rem 2rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.8rem;

  &:hover:not(:disabled) {
    background: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const TransferMessage = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: 1rem 1.5rem;
  border-radius: 4px;
  font-size: 1.3rem;
  font-weight: 500;
  margin-top: 1rem;
  background: ${props => props.type === 'success' ? '#d4edda' : props.type === 'warning' ? '#fff3cd' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'warning' ? '#856404' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'warning' ? '#ffeeba' : '#f5c6cb'};
`

const MtLookupSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid ${theme.color.border.light};
`

const MtLookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`

const MtLookupLabel = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const MtLookupValue = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  font-family: monospace;
`

const MtLookupButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const SyncButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const MismatchBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  background: #f8d7da;
  color: #721c24;
`

const MatchBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  background: #d4edda;
  color: #155724;
`

interface PlatformUserIDsProps {
  userData: any
  marianaTekData: any
  barrysPlatformData: any
}

const PlatformUserIDs: React.FC<PlatformUserIDsProps> = ({ userData, marianaTekData, barrysPlatformData }) => {
  const [transferFrom, setTransferFrom] = useState<'sessionm' | 'mariana_tek' | 'middleware'>('mariana_tek')
  const [transferTo, setTransferTo] = useState<'sessionm' | 'mariana_tek' | 'middleware'>('sessionm')
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferMessage, setTransferMessage] = useState<{ type: 'success' | 'error' | 'warning', text: string } | null>(null)

  // MT lookup state
  const [mtLookupId, setMtLookupId] = useState<string | null>(null)
  const [isLookingUp, setIsLookingUp] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [mtLookupError, setMtLookupError] = useState<string | null>(null)

  const user = userData?.user

  // Auto-lookup MT ID when user data loads
  useEffect(() => {
    if (user?.email) {
      handleMtLookup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  if (!userData || !user) {
    return null
  }

  const handleMtLookup = async () => {
    if (!user?.email) return
    setIsLookingUp(true)
    setMtLookupError(null)
    try {
      const result = await lookupMarianaTekUser(user.email)
      const mtUser = result?.data?.[0]
      if (mtUser) {
        setMtLookupId(mtUser.id)
      } else {
        setMtLookupId(null)
        setMtLookupError('No Mariana Tek account found for this email')
      }
    } catch (err: any) {
      setMtLookupError(err.message || 'Lookup failed')
      setMtLookupId(null)
    } finally {
      setIsLookingUp(false)
    }
  }

  const handleSyncMtIdToSessionM = async () => {
    if (!mtLookupId || !user?.id) return
    if (!window.confirm(`Update SessionM external_id to ${mtLookupId}? This will overwrite the current value (${user.external_id || 'none'}).`)) return

    setIsSyncing(true)
    setTransferMessage(null)
    try {
      await transferIdToSessionM(user.id, mtLookupId, 'mariana_tek')
      setTransferMessage({
        type: 'success',
        text: `SessionM updated with Mariana Tek ID: ${mtLookupId}`
      })
    } catch (err: any) {
      setTransferMessage({
        type: 'error',
        text: err.message || 'Failed to sync MT ID to SessionM'
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const sessionMHasMtId = !!user.external_id
  const mtIdMatches = mtLookupId && user.external_id && mtLookupId === user.external_id

  const handleTransfer = async () => {
    setTransferMessage(null)

    // Validate transfer
    if (transferFrom === transferTo) {
      setTransferMessage({ type: 'error', text: 'Cannot transfer to the same platform' })
      return
    }

    // Only SessionM is supported as destination for now
    if (transferTo !== 'sessionm') {
      setTransferMessage({ type: 'error', text: 'Only transfers TO SessionM are currently supported' })
      return
    }

    try {
      setIsTransferring(true)

      // Get the source ID to transfer
      let externalId = ''
      let externalIdType: 'mariana_tek' | 'barrys_platform' = 'mariana_tek'

      if (transferFrom === 'mariana_tek') {
        externalId = user.external_id
        externalIdType = 'mariana_tek'
      } else if (transferFrom === 'middleware') {
        externalId = barrysPlatformData?.id || ''
        externalIdType = 'barrys_platform'
      }

      if (!externalId) {
        setTransferMessage({ type: 'error', text: `No ${transferFrom} ID found to transfer` })
        setIsTransferring(false)
        return
      }

      // Transfer to SessionM
      await transferIdToSessionM(user.id, externalId, externalIdType)

      setTransferMessage({
        type: 'success',
        text: `Successfully transferred ${transferFrom} ID to SessionM`
      })
    } catch (error) {
      console.error('Transfer failed:', error)
      setTransferMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Transfer failed'
      })
    } finally {
      setIsTransferring(false)
    }
  }

  return (
    <PlatformIDsSection>
      <PlatformIDsTitle>Platform User IDs</PlatformIDsTitle>

      <MtLookupSection>
        <MtLookupRow>
          <MtLookupLabel>Mariana Tek ID (from email lookup):</MtLookupLabel>
          {isLookingUp ? (
            <MtLookupValue>Looking up...</MtLookupValue>
          ) : mtLookupError ? (
            <MtLookupValue style={{ color: '#dc3545' }}>{mtLookupError}</MtLookupValue>
          ) : mtLookupId ? (
            <MtLookupValue>{mtLookupId}</MtLookupValue>
          ) : (
            <MtLookupValue style={{ color: '#999' }}>Not looked up</MtLookupValue>
          )}
          <MtLookupButton onClick={handleMtLookup} disabled={isLookingUp}>
            {isLookingUp ? 'Looking up...' : 'Refresh Lookup'}
          </MtLookupButton>
        </MtLookupRow>

        {mtLookupId && (
          <MtLookupRow style={{ marginTop: '1rem' }}>
            <MtLookupLabel>SessionM external_id:</MtLookupLabel>
            <MtLookupValue>{user.external_id || 'NOT SET'}</MtLookupValue>
            {mtIdMatches ? (
              <MatchBadge>Match</MatchBadge>
            ) : (
              <>
                <MismatchBadge>{sessionMHasMtId ? 'Mismatch' : 'Missing'}</MismatchBadge>
                <SyncButton onClick={handleSyncMtIdToSessionM} disabled={isSyncing}>
                  {isSyncing ? 'Syncing...' : `Sync ${mtLookupId} → SessionM`}
                </SyncButton>
              </>
            )}
          </MtLookupRow>
        )}
      </MtLookupSection>

      <PlatformIDsTable>
        <thead>
          <tr>
            <TableHeader>Platform</TableHeader>
            <TableHeader>SessionM ID</TableHeader>
            <TableHeader>Mariana Tek ID</TableHeader>
            <TableHeader>Barry's Platform ID</TableHeader>
          </tr>
        </thead>
        <tbody>
          <TableRow>
            <PlatformName>Mariana Tek</PlatformName>
            <TableCell>N/A</TableCell>
            <TableCell>{user.external_id || 'N/A'}</TableCell>
            <TableCell>{marianaTekData?.data?.[0]?.relationships?.user?.data?.id || 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <PlatformName>SessionM</PlatformName>
            <TableCell>{user.id || 'N/A'}</TableCell>
            <TableCell>{user.external_id || 'N/A'}</TableCell>
            <TableCell>{barrysPlatformData?.id || 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <PlatformName>Middleware</PlatformName>
            <TableCell>{barrysPlatformData?.session_m_id || 'N/A'}</TableCell>
            <TableCell>{barrysPlatformData?.mariana_tek_id || user.external_id || 'N/A'}</TableCell>
            <TableCell>{barrysPlatformData?.id || 'N/A'}</TableCell>
          </TableRow>
        </tbody>
      </PlatformIDsTable>

      <TransferSection>
        <TransferTitle>Transfer ID(s)</TransferTitle>
        <SelectGroup>
          <SelectLabel>From</SelectLabel>
          <Select
            value={transferFrom}
            onChange={(e) => setTransferFrom(e.target.value as 'sessionm' | 'mariana_tek' | 'middleware')}
            disabled={isTransferring}
          >
            <option value="sessionm">SessionM</option>
            <option value="mariana_tek">Mariana Tek</option>
            <option value="middleware">Middleware</option>
          </Select>
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>To</SelectLabel>
          <Select
            value={transferTo}
            onChange={(e) => setTransferTo(e.target.value as 'sessionm' | 'mariana_tek' | 'middleware')}
            disabled={isTransferring}
          >
            <option value="sessionm">SessionM</option>
            <option value="mariana_tek">Mariana Tek</option>
            <option value="middleware">Middleware</option>
          </Select>
        </SelectGroup>
        <TransferButton onClick={handleTransfer} disabled={isTransferring}>
          {isTransferring ? 'Transferring...' : 'Transfer'}
        </TransferButton>
      </TransferSection>

      {transferMessage && (
        <TransferMessage type={transferMessage.type}>
          {transferMessage.text}
        </TransferMessage>
      )}
    </PlatformIDsSection>
  )
}

export default PlatformUserIDs
